<template>
<div>
  <va-modal
    id="contactDetailsModal"
    title=""
    size="medium"
    class="evollu-form"
    :value="showContactDetailsModal"
    :noOutsideDismiss="true"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.cancel')"
    :dangerText="$t('modals.buttons.delete')"
    :showDelete="editingLead != null"
    @ok="saveLead"
    @cancel="resetFields"
    @remove="deleteLead">

    <div slot="header" class="modal-title">
      {{getModalTitle()}}
    </div>

    <div class="row" v-if="!editingLead && entitiesWrite.length > 1">
      <div class="col-12">
        <label class="form-label">{{$t('references.inputs.entity')}}</label>
        <div class="form-group">
          <div class="input-group">
            <multiselect
              v-model="form.entity"
              :options="entitiesWrite"
              track-by="id"
              label="name"
              :custom-label="nameWithCity"
              :placeholder="$t('references.inputs.select_entity')"
              :allow-empty="false"
              @input="onEntitySelected"
              :multiple="false"
              :searchable="true"
              :disabled="!!editingLead"
              >
              <span slot="noResult">{{'patients.noResults'| translate}}</span>
            </multiselect>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="form.entity && facilitiesFromSelectedEntity.length > 1">
      <div class="col">
        <label class="form-label">{{$t('references.inputs.select_facility')}}</label>
        <div class="form-group">
          <div class="input-group">
            <multiselect
              v-model="form.facility"
              :options="facilitiesFromSelectedEntity"
              track-by="id"
              label="name"
              select-label=""
              :custom-label="nameWithCity"
              selected-label=""
              deselect-label=""
              :placeholder="$t('references.inputs.select_facility')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="!!editingLead"
              >
            </multiselect>
          </div>
        </div>
      </div>
    </div>

    <div class="newContactModal">
      <fieldset>
        <div class="row">
          <div class="col">
            <label class="form-label">{{$t('references.inputs.personal_info')}}</label>
            <div class="form-group">
              <div class="input-group">
                <va-input
                  :placeholder="$t('references.inputs.name')"
                  v-model="form.name"
                  id="editing-name"
                  required/>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  :placeholder="$t('references.inputs.surname')"
                  v-model="form.surname"
                  id="editing-surname"
                  required/>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <div class="form-group with-icon-right">
              <div class="input-group">
                <DatePicker
                  id="appointment-start"
                  class="w-100"
                  v-model="form.birthdate"
                  :config="datePickerConfig"
                  :placeholder="$t('leads.details.birthdate')"
                />
              </div>
            </div>
          </div>

          <div class="col-6">
            <div class="form-group">
              <div class="input-group">
                <multiselect
                  v-model="form.gender"
                  :options="genderOptions"
                  track-by="id"
                  label="translate"
                  :custom-label="opts => $t(opts.translate)"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  :placeholder="$t('wellbeing.leads.gender')"
                  :allow-empty="true"
                  :multiple="false"
                  :searchable="false"
                >
                </multiselect>
              </div>
            </div>
          </div>

        </div>

        <div class="row">
          <div class="col-6">
            <div class="form-group" style="margin-bottom:0.25rem;">
              <div class="input-group">
                <vue-tel-input
                  :key="vueTelInputRedrawIndex"
                  v-model="form.phone"
                  inputId="phone-input-1"
                  class="w-100"
                  maxlength="20"
                  :preferredCountries="$store.state.countries.allowed"
                  :disabledFetchingCountry="true"
                  :defaultCountry="defaultCountrySelection"
                  :placeholder="$t('references.inputs.mobile_phone')"
                  @input="(value, options) => onEditingPhoneInput('phone', value, options)">
                </vue-tel-input>
              </div>
            </div>
            <div class="has-error form-group" style="margin-bottom:0rem;">
              <small v-show="invalidPhone" class="help text-danger pl-2" style="font-size: 0.8rem;">
                <i class="fa fa-exclamation-triangle error-icon icon-right"></i>
                {{'references.errors.invalidPhone' | translate}}
              </small>
            </div>
          </div>

          <div class="col-6">
            <div class="form-group with-icon-right" >
              <div class="input-group">
                <va-input
                  id="successfulEmail"
                  name="successfulEmail"
                  v-model="form.email"
                  :placeholder="'Email ('+$t('references.inputs.optional')+')'"
                  :error="invalidEmail && form.email !== ''"
                  :error-messages="[$t('users.inputs.invalid_email')]"
                />
              </div>
            </div>
          </div>

        </div>


      </fieldset>

      <div class="row">
        <fieldset class="w-100">
          <div class="col">
            <label class="form-label">{{$t('leads.details.address')}}</label>
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="form.addressLine1"
                  :placeholder="$t('wellbeing.inputs.address_line_1')"/>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="form.addressLine2"
                  :placeholder="$t('wellbeing.inputs.address_line_2')"/>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="form.addressPostalCode"
                  :placeholder="$t('references.inputs.address_postal_code')"/>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="form-group">
              <div class="input-group">
                <va-input
                  v-model="
                  form.addressCity"
                  :placeholder="$t('references.inputs.address_city')"/>
              </div>
            </div>
          </div>

        </fieldset>
      </div>


      <div class="row">
        <div class="col">
          <label class="form-label">{{'wellbeing.appointmentPreferences.title'| translate}}:</label>
          <div class="form-group">
            <div class="input-group">
              <multiselect
                v-model="form.appointmentPreference"
                :options="appointmentPreferences"
                track-by="id"
                label="translate"
                :custom-label="prefs => $t(prefs.translate)"
                select-label=""
                selected-label=""
                deselect-label=""
                :placeholder="$t('references.contactBestTime.select_option')"
                :allow-empty="true"
                :multiple="false"
                :searchable="false"
                >

              </multiselect>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <label class="form-label">{{'wellbeing.leads.simptoms'| translate}}:</label>
          <div class="form-group">
            <div class="input-group">
              <multiselect
                v-model="form.simptoms"
                :options="simptomsOptions"
                track-by="id"
                label="translate"
                :custom-label="opts => $t(opts.translate)"
                select-label=""
                selected-label=""
                deselect-label=""
                :placeholder="$t('wellbeing.simptoms.choose_simptoms')"
                :allow-empty="true"
                :multiple="true"
                :searchable="false"
              >
              </multiselect>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="form-group">
            <div class="input-group">
              <va-input
                :placeholder="$t('leads.details.comment')"
                type="textarea"
                :maxlength="250"
                v-model="form.comments"
                required/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </va-modal>

  <va-modal
    v-model="showDeleteContactModal"
    size="small"
    title=""
    :noOutsideDismiss="true"
    :okText="'references.inputs.delete' | translate"
    :cancelText="'modal.cancel' | translate"
    @cancel="closeDelete"
    @ok="confirmDelete"
    >
    <div slot="header">
      <p style="font-size:18px;"><b>{{$t('references.delete?')}}</b></p>
    </div>
    <div v-if="editingLead">
      <div style="margin-bottom:1.25rem;">{{'references.delete_message' | translate}} {{form.name}} {{form.surname}}?</div>

    </div>
  </va-modal>
</div>
</template>

<script>
import moment from 'moment'
import Multiselect from 'vue-multiselect'
import { VueTelInput } from 'vue-tel-input'
import { EMAIL_REGEX } from '@/utils/regex'
import DatePicker from "@/components/common/DatePicker.vue";
import { mapGetters } from "vuex";
import { WellbeingService } from "@/api/WellbeingService";

export default {
  props: ['editingLead', 'entitiesWrite', 'entities', 'facilitiesWrite', 'service'],
  components: {
    DatePicker,
    Multiselect,
    VueTelInput
  },
  data () {
    return {
      editingPhoneValid: true,
      editingPhone2Valid: true,
      showContactDetailsModal: false,
      showDeleteContactModal: false,
      vueTelInputRedrawIndex: 1,
      form: {
        name: '',
        surname: '',
        email: '',
        phone: '',
        birthdate: '',
        addressLine1: '',
        addressLine2: '',
        addressPostalCode: '',
        addressCity: '',
        appointmentPreference: null,
        entity: null,
        entityId: null,
        facilityId: null,
        comments: '',
        simptoms: [],
        gender: null
      },
      deletedReason: '',
    }
  },
  mounted () {
    if (this.editingLead) {
      this.updateLead()
    } else {
      if (this.entitiesWrite.length === 1) {
        this.form.entity = this.entitiesWrite[0]
      }
    }
    this.showContactDetailsModal = true
    this.showDeleteContactModal = false
  },
  computed: {
    ...mapGetters({
      appointmentPreferences: 'wellbeing/appointmentPreferences',
      simptomsOptions: 'wellbeing/simptomsOptions',
      genderOptions: 'wellbeing/genderOptions'
    }),
    datePickerConfig: function () {
      return {
        enableTime: false,
        maxDate: moment().format('YYYY-MM-DD'),
        noCalendar: false,
        dateFormat: 'Y-m-d'
      }
    },
    isUpdating: function () {
      return this.editingLead !== null
    },
    facilitiesFromSelectedEntity: function () {
      if (!this.form.entity) {
        return null
      }
      return this.facilitiesWrite.filter(facility => facility.entity_id === this.form.entity.id)
    },
    authUserEntity: function () {
      return this.$auth.user().entity
    },
    defaultCountrySelection: function () {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country
      }
      return this.$store.state.countries.default
    },

    invalidPhone: function () {
      if (this.form.phone) {
        return !this.editingPhoneValid
        // return !this.editingPhone.replace(/\s/g, '').match(/[9,2]\d{8,20}$/) || !this.editingPhoneValid
      }
      return false
    },

    invalidEmail: function () {
      if (this.form.email) {
        return !this.form.email.match(EMAIL_REGEX)
      }
      return false
    }
  },
  methods: {
    onEditingPhoneInput: function (field, value, options) {
      if (field === 'phone') {
        this.editingPhoneValid = options.valid
      } else {
        this.editingPhone2Valid = options.valid
      }
    },
    onEntitySelected: function (item) {
      if (item.facilities.length) {
        this.form.facility = item.facilities[0]
      }
    },
    saveLead: function () {
      let data = this.getFormData()

      let errors = Object.values(this.getFormErrors(data))

      if (errors.length) {
        this.showToast(errors[0], toastErrorOptions)
        return
      }

      this.isUpdating ? this.update(data) : this.save(data)
    },
    getFormErrors: function (data) {
      let errors = {}

      if (!data.name || !data.phone) {
        errors['name_phone'] = this.$t('references.errors.no_name_phone')
      }

      if (this.invalidPhone ) {
        errors['phone'] = this.$t('references.errors.invalidPhone')
      }

      return errors
    },
    save: async function(data) {
      this.isloading = true
      this.$store.commit('SET_LOADING', true)

      try {
        const savedLead = await WellbeingService.saveContact(data)
        this.$store.commit('SET_LOADING', false)
        this.isloading = false
        this.showToast(this.$t('references.errors.success'), this.$store.state.successToastOptions)
        this.$emit('close-contact-modal')
        this.$emit('update-table', savedLead)
      } catch (error) {
        this.isloading = false
        this.showToast(this.$t('references.errors.server'), this.$store.state.errorToastOptions)
        console.log(error)
      }
    },
    update: async function(data) {
      this.isloading = true
      this.$store.commit('SET_LOADING', true)
      try {
        const updatedLead = await WellbeingService.updateContact(this.editingLead.id, data)
        this.$store.commit('SET_LOADING', false)
        this.isloading = false
        this.showToast(this.$t('references.errors.success'), this.$store.state.successToastOptions)
        this.$emit('update-table', updatedLead)
        this.showContactDetailsModal = false
      } catch (error) {
        this.isloading = false
        this.showToast(this.$t('references.errors.server'), this.$store.state.errorToastOptions)
        console.log(error)
      }
    },
    getFormData: function () {
      let form = JSON.parse(JSON.stringify(this.form))

      let body = Object.assign(form, {
        phone: this.form.phone ? this.form.phone.replace(/\s/g, '') : null,
        email: this.form.email ? this.form.email.toLowerCase() : '',
        birthdate: this.form.birthdate ? moment(this.form.birthdate, 'YYYY-MM-DD', true).format('YYYY-MM-DD') : null,
        appointmentPreferenceId: this.form.appointmentPreference ? this.form.appointmentPreference.id : null,
        simptomsIds: this.form.simptoms.length > 0 ? this.form.simptoms.map(elem => elem.id).join(',') : null,
        gender: this.form.gender ? this.form.gender.value : null
      })


      if (!this.isUpdating) {
        if (body.entity) {
          body.entityId = parseInt(body.entity.id)
          delete body.entity
          body.facilityId = body.facility.id
        }

        body.redirect_uri = window.location.href.split('#')[0]
      }
      body.entityId = parseInt(body.entityId)
      body.facilityId = parseInt(body.facilityId)
      return body
    },
    resetFields: function () {
      this.$emit('close-contact-modal')
    },
    nameWithCity ({ name, cities }) {
      if (cities == null || cities.length < 1) return name
      return name + ' (' + cities.join(', ') + ')'
    },
    deleteLead: function () {
      if (this.editingLead) {
        this.showDeleteContactModal = true
      }
    },
    closeDelete: function () {
      //this.deletedReason = ''
      this.showDeleteContactModal = false
      this.showContactDetailsModal = true
    },
    confirmDelete: async function() {
      try {
        const deletedLead = await WellbeingService.deleteContact(this.editingLead.id)

        this.showDeleteContactModal = false
        this.resetFields()
        this.$emit('update-table')
        this.showToast(this.$t('references.errors.delete_success'), this.$store.state.successToastOptions)
      } catch (error) {
        this.showToast(this.$t('references.errors.delete_error'), this.$store.state.errorToastOptions)
        this.showDeleteContactModal = false
        console.log(error)
      }

    },
    getModalTitle: function () {
      if (this.editingLead) {
        return `${this.$t('references.edit')} ${this.form.name}`
      }
      if (this.entitiesWrite.length > 1) {
        return this.$t('references.add')
      }
      return `${this.$t('references.add')} ${this.entitiesWrite[0].name}`
    },
    updateLead: function () {
      this.form = JSON.parse(JSON.stringify(this.editingLead))
      this.form.facility = this.facilitiesWrite.find(facility => facility.id === this.editingLead.facility_id)
      this.form.entity = this.getFullEntity(this.editingLead.entity_id)
      if(this.editingLead.appointmentPreference !== null) {
        this.form.appointmentPreference = this.getFullPeriod(parseInt(this.editingLead.appointmentPreference.id))
      } else {
        this.form.appointmentPreference = null
      }
      this.form.simptoms = this.simptomsOptions.filter(opt => {
        return this.editingLead.simptoms.map(elem => parseInt(elem.id)).includes(opt.id)
      })
      if(this.form.gender) {
        this.form.gender = this.genderOptions.filter(elem => elem.value === this.form.gender)[0]
      }
    },
    writableEntity: function (entityId) {
      this.entitiesWrite.some(entity => entity.id === entityId)
    },
    getFullEntity (entityId) {
      return this.entities.find(entity => entity.id === entityId)
    },
    getFullPeriod (option) {
      return this.appointmentPreferences.find(_option => _option.id === option)
    }
  },
  watch: {
    'form.entity': {
      handler: function (entity) {
        this.vueTelInputRedrawIndex++
      },
      deep: true
    },
    entitiesWrite: {
      handler: function () {
        if (this.writableEntity(this.authUserEntity)) {
          this.form.entity = this.authUserEntity
        } else {
          this.form.entity = this.entitiesWrite[0]
        }
        if (this.form.entity.facilities.length) {
          this.form.facility = this.form.entity.facilities[0]
        }
        if (this.editingLead && this.editingLead.appointmentPreference !== null) {
          this.form.appointmentPreference = this.appointmentPreferences.find(pref => pref.id === parseInt(this.editingLead.appointmentPreference.id))
        }
      },
      immediate: true
    }
  },
}
</script>

<style lang="scss">

.newContactModal .form-group .control-label{
  z-index: 0;
}

.multiselect .multiselect__tag {
  background-color: #29c4a9;
}

.multiselect .multiselect__option--highlight {
  background-color: #29c4a9;
}

.multiselect .multiselect__option--highlight::after {
  background-color: #29c4a9;
}

.multiselect .multiselect__option--selected {
  background-color: #d9d9d9;
}

.multiselect .multiselect__option--selected.multiselect__option--highlight {
  background-color: #e34a4a;
}

.multiselect .multiselect__option--selected.multiselect__option--highlight::after {
  background-color: #e34a4a;
}

.multiselect__tag-icon:focus, .multiselect__tag-icon:hover {
  background: #21ab93 !important;
}

.contacts-checkboxes {
  margin-bottom: 2.5rem;
  .abc-checkbox {
    margin-bottom: 10px;
  }
}

</style>
<style lang="scss" scoped>
.evollu-form {
  .row {
    margin-bottom: 0 !important;
  }
}
</style>
